<template>
  <div class="saved-grid-wrap">
    <!-- Optional header actions -->
    <div class="saved-grid-toolbar">
      <div class="title">Saved Items</div>
      <div class="spacer" />
      <v-btn small text class="toolbar-btn" @click="$emit('refresh')">
        <v-icon left small>mdi-refresh</v-icon>Refresh
      </v-btn>
    </div>

    <!-- Empty state -->
    <div v-if="!savedItems || !savedItems.length" class="empty">
      <v-icon large class="mb-2">mdi-bookmark-outline</v-icon>
      Nothing saved yet.
    </div>

    <!-- Responsive grid -->
    <ul class="saved-grid">
      <li
        v-for="(item, index) in savedItems"
        :key="index"
        class="card"
        :class="`type-${item.type}`"
      >
        <!-- Badge + date -->
        <div class="card-top">
          <span class="badge" :class="badgeClass(item.type)">
            <v-icon x-small class="mr-1">
              {{ item.type === 'image' ? 'mdi-image-multiple' : 'mdi-file-document-outline' }}
            </v-icon>
            {{ item.type }}
          </span>
          <span class="date">{{ formatDate(item.savedAt || item.date) }}</span>
        </div>

        <!-- IMAGE CARD -->
        <div v-if="item.type === 'image'" class="media">
          <img
            class="thumb"
            :src="item.url"
            alt="Saved Image"
            loading="lazy"
            decoding="async"
            @error="onImgError($event)"
          />
        </div>
        <div v-if="item.type === 'image'" class="body">
          <div class="prompt" :title="item.prompt">{{ item.prompt }}</div>
        </div>

        <!-- QUERY CARD -->
        <div v-else class="body">
          <div class="row line">
            <v-icon left small class="muted">mdi-calendar</v-icon>
            <span class="muted">{{ formatDate(item.savedAt || item.date) }}</span>
          </div>
          <div class="row">
            <v-icon left small class="muted">mdi-cog</v-icon>
            <strong class="label">Preset:</strong>
            <span class="value ml-1">{{ (item.preset || '').toUpperCase() }}</span>
          </div>
          <div class="row mt-1" v-if="item.preset === 'search'">
            <v-icon left small class="muted">mdi-magnify</v-icon>
            <span class="clamp" :title="item.searchItem">Search: {{ item.searchItem }}</span>
          </div>
          <div class="row mt-1" v-else-if="item.preset === 'report'">
            <v-icon left small class="muted">mdi-file-chart</v-icon>
            <span class="clamp" :title="item.subject">Report on: {{ item.subject }}</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="actions">
          <v-btn small class="btn" @click="$emit('edit-item', index)">
            <v-icon left small>mdi-pencil</v-icon>Edit
          </v-btn>
          <v-btn small class="btn danger" @click="$emit('delete-item', index)">
            <v-icon left small>mdi-delete</v-icon>Delete
          </v-btn>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "SavedItemsGrid",
  props: {
    savedItems: {
      type: Array,
      default: () => [],
    },
  },
  methods: {
    formatDate(v) {
      if (!v) return "";
      try {
        const d = typeof v === "number" ? new Date(v) : new Date(String(v));
        return d.toLocaleString();
      } catch {
        return String(v);
      }
    },
    badgeClass(type) {
      return type === "image" ? "badge-image" : "badge-query";
    },
    onImgError(e) {
      e.target.classList.add("broken");
      e.target.alt = "Image unavailable";
    },
  },
};
</script>

<style scoped>
/* grid shell */
.saved-grid-wrap {
  --glass: rgba(255,255,255,0.08);
  --glass-2: rgba(255,255,255,0.12);
  --ink: #e9f5ff;
  --ink-dim: #a9c1cc;
  --accent: #42da91;
  --accent-2: #78cce2;
  color: var(--ink);
}

/* toolbar */
.saved-grid-toolbar {
  display: flex; align-items: center; margin-bottom: 10px;
}
.saved-grid-toolbar .title {
  font-weight: 700; letter-spacing: .02em;
}
.saved-grid-toolbar .spacer { flex: 1; }
.toolbar-btn { opacity: .9; }

/* empty */
.empty {
  display: grid; place-items: center; text-align: center;
  padding: 30px; border: 1px dashed rgba(255,255,255,0.12);
  border-radius: 12px; color: var(--ink-dim);
}

/* responsive grid */
.saved-grid {
  list-style: none; padding: 0; margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  grid-gap: 14px;
}

/* card */
.card {
  position: relative;
  display: flex; flex-direction: column;
  background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03));
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 14px;
  padding: 12px;
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
  box-shadow: 0 10px 18px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.03);
  overflow: hidden;
}
.card:hover {
  transform: translateY(-2px);
  border-color: rgba(66,218,145,0.35);
  box-shadow: 0 16px 24px rgba(0,0,0,0.45), 0 0 0 1px rgba(66,218,145,0.18) inset;
}

/* top row */
.card-top {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 8px;
}
.badge {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 8px; font-size: 11px; text-transform: uppercase;
  letter-spacing: .06em; border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.16);
  background: rgba(255,255,255,0.08);
}
.badge-image { border-color: rgba(66,218,145,0.45); }
.badge-query { border-color: rgba(120,204,226,0.45); }
.date { color: var(--ink-dim); font-size: 12px; }

/* image */
.media {
  border-radius: 10px; overflow: hidden;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.08);
}
.thumb {
  width: 100%; aspect-ratio: 4/3; object-fit: cover; display: block;
  transition: transform .3s ease, filter .3s ease, opacity .3s ease;
}
.thumb:hover { transform: scale(1.02); }
.thumb.broken { opacity: .6; filter: grayscale(80%); }

/* body */
.body { padding-top: 8px; }
.prompt {
  color: var(--ink); font-size: 13.5px; line-height: 1.45;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.row { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
.line { border-bottom: 1px dashed rgba(255,255,255,0.08); padding-bottom: 4px; margin-bottom: 6px; }
.muted { color: var(--ink-dim); }
.label { color: var(--ink); }
.value { color: var(--ink); opacity: .95; }
.clamp { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

/* actions */
.actions {
  display: flex; gap: 8px; margin-top: 10px;
}
.btn {
  background: rgba(255,255,255,0.10) !important;
  border: 1px solid rgba(255,255,255,0.20) !important;
  color: #fff !important;
  border-radius: 10px !important;
  transition: transform .12s ease, box-shadow .2s ease !important;
}
.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.35);
  border-color: rgba(66,218,145,0.45) !important;
}
.btn.danger {
  border-color: rgba(255,93,115,0.45) !important;
}

/* small screens: a bit larger tap targets */
@media (max-width: 600px) {
  .saved-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    grid-gap: 12px;
  }
  .btn { padding: 0 10px !important; }
}
</style>
